import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Descriptions, Divider, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import ridePricingApi from 'api/ridePricingApi';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import ToastHelper from 'general/helpers/ToastHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
const { Text, Title } = Typography;
const STATUS = { DRAFT: ['Bản nháp', 'default'], ACTIVE: ['Đang áp dụng', 'success'], ARCHIVED: ['Đã lưu trữ', 'warning'] };
const money = (value) => value == null ? '—' : `${Number(value).toLocaleString('vi-VN')} ₫`;
const statusTag = (value) => <Tag color={(STATUS[value] || [null, 'default'])[1]}>{(STATUS[value] || [value])[0]}</Tag>;
const TIME_PATTERN = /^([01]\d|2[0-4]):[0-5]\d$/;

// Pilot hành lang (CORRIDOR) đang hỗ trợ — mỗi hành lang có 1 giá cố định/ghế,
// không tính theo km. Thêm hành lang mới ở đây khi backend hỗ trợ thêm scopeCode.
const CORRIDOR_OPTIONS = [
  { value: 'HANOI_OLD_NAM_DINH', label: 'Hà Nội ↔ Nam Định' },
];
const corridorLabel = (scopeCode) => CORRIDOR_OPTIONS.find((option) => option.value === scopeCode)?.label || scopeCode;

const defaults = {
  name: '', scopeType: 'GLOBAL', scopeCode: null, flatFarePerSeat: null, priority: 0, baseFare: 0, includedDistanceMeters: 0,
  pricePerKm: 0, pricePerMinute: 0, minimumFarePerSeat: 0, maximumFarePerSeat: null,
  roundingUnit: 1000, surgeEnabled: false, surgeMultiplier: 1, totalPlatformFeePercent: 0,
  platformFeePercent: 0, referralCommissionPercent: 0, charterPricePercent: 100, charterPricePercent4: 100, charterPricePercent7: 100,
};

// Same numbers as evgapi services/ride/ridePricingPolicy.js's DEFAULT_* —
// shown here so admins see (and can tweak) what's actually in effect today
// even on a policy that has never set its own configuration.timeBandMultipliers.
const DEFAULT_TIME_BANDS = [
  { from: '05:00', to: '07:00', multiplier: 1.00 },
  { from: '07:00', to: '09:00', multiplier: 1.05 },
  { from: '09:00', to: '15:00', multiplier: 1.00 },
  { from: '15:00', to: '18:00', multiplier: 1.05 },
  { from: '18:00', to: '21:00', multiplier: 1.10 },
  { from: '21:00', to: '24:00', multiplier: 1.15 },
  { from: '00:00', to: '05:00', multiplier: 1.20 },
];
const DEFAULT_DETOUR_TIERS_KM = [
  { maxKm: 5, surcharge: 0 },
  { maxKm: 10, surcharge: 20000 },
  { maxKm: 15, surcharge: 30000 },
  { maxKm: 20, surcharge: 50000 },
];
const DEFAULT_DEMAND_BAND_PERCENTS = {
  floor: 88, earlyBooking: [91.6, 95.6], standard: 100, busy: [107.6, 111.6], peak: [115.6, 119.6], ceiling: 128, lastSeat: [87.6, 95.6],
};

const DemandScenarioCard = ({ title, condition, priority, tagColor, field }) => (
  <div
    style={{
      background: '#F8FAFC',
      border: '1px solid #E2E8F0',
      borderRadius: 8,
      padding: '12px 14px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}
  >
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>{title}</div>
        <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{condition}</div>
      </div>
      <Tag color={tagColor} style={{ marginRight: 0, fontSize: 11 }}>
        {priority}
      </Tag>
    </div>
    <Row gutter={8} align="middle">
      <Col span={11}>
        <Form.Item
          name={['configuration', 'demandBandPercents', field, 0]}
          noStyle
          rules={[{ required: true, message: 'Nhập %' }]}
        >
          <InputNumber min={1} max={500} step={0.1} addonBefore="Từ" addonAfter="%" style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={2} className="text-center text-muted" style={{ fontWeight: 600 }}>
        –
      </Col>
      <Col span={11}>
        <Form.Item
          name={['configuration', 'demandBandPercents', field, 1]}
          noStyle
          rules={[{ required: true, message: 'Nhập %' }]}
        >
          <InputNumber min={1} max={500} step={0.1} addonBefore="Đến" addonAfter="%" style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
  </div>
);

// Converts a policy's raw `configuration` JSON (backend shape) into the
// nested form-field shape this modal edits. Arrays/tables that were never
// configured fall back to the same doc-default the backend itself falls
// back to, so admins always see what's really in effect, not a blank table.
const configToFormValues = (configuration = {}) => ({
  timeBandMultipliers: Array.isArray(configuration.timeBandMultipliers) && configuration.timeBandMultipliers.length
    ? configuration.timeBandMultipliers : DEFAULT_TIME_BANDS,
  detourSurchargeTiersKm: (Array.isArray(configuration.detourSurchargeTiers) && configuration.detourSurchargeTiers.length
    ? configuration.detourSurchargeTiers.map((tier) => ({ maxKm: Number(tier.maxMeters) / 1000, surcharge: Number(tier.surcharge) }))
    : DEFAULT_DETOUR_TIERS_KM),
  seatCapacityMultipliersList: Object.entries(configuration.seatCapacityMultipliers || {})
    .map(([seats, multiplier]) => ({ seats: Number(seats), multiplier: Number(multiplier) })),
  demandBandPercentsEnabled: !!configuration.demandBandPercents,
  demandBandPercents: configuration.demandBandPercents || DEFAULT_DEMAND_BAND_PERCENTS,
});

// The inverse: reads the modal's form values and produces the backend
// `configuration` JSON, preserving any unrelated keys the JSON already had
// (e.g. free-text notes) that this form doesn't manage.
const buildConfiguration = (values, previousConfiguration) => {
  const configuration = { ...(previousConfiguration || {}) };
  const cfg = values.configuration || {};

  const timeBands = (cfg.timeBandMultipliers || [])
    .filter((row) => row && row.from && row.to && row.multiplier != null)
    .map((row) => ({ from: row.from, to: row.to, multiplier: Number(row.multiplier) }));
  if (timeBands.length) configuration.timeBandMultipliers = timeBands; else delete configuration.timeBandMultipliers;

  const detourTiers = (cfg.detourSurchargeTiersKm || [])
    .filter((row) => row && row.maxKm != null && row.surcharge != null)
    .map((row) => ({ maxMeters: Math.round(Number(row.maxKm) * 1000), surcharge: Number(row.surcharge) }));
  if (detourTiers.length) configuration.detourSurchargeTiers = detourTiers; else delete configuration.detourSurchargeTiers;

  const seatRows = (cfg.seatCapacityMultipliersList || []).filter((row) => row && row.seats != null && row.multiplier != null);
  if (seatRows.length) {
    configuration.seatCapacityMultipliers = Object.fromEntries(seatRows.map((row) => [String(Math.round(Number(row.seats))), Number(row.multiplier)]));
  } else delete configuration.seatCapacityMultipliers;

  if (cfg.demandBandPercentsEnabled) {
    const d = cfg.demandBandPercents || {};
    configuration.demandBandPercents = {
      floor: Number(d.floor), earlyBooking: [Number(d.earlyBooking?.[0]), Number(d.earlyBooking?.[1])], standard: 100,
      busy: [Number(d.busy?.[0]), Number(d.busy?.[1])], peak: [Number(d.peak?.[0]), Number(d.peak?.[1])],
      ceiling: Number(d.ceiling), lastSeat: [Number(d.lastSeat?.[0]), Number(d.lastSeat?.[1])],
    };
  } else delete configuration.demandBandPercents;

  // Removed from the form entirely (mục 10/11) — strip unconditionally so a
  // draft/policy copied or re-saved from one that still had these set (from
  // before the removal) can't silently keep charging/discounting through a
  // field the admin no longer has any CMS control over.
  delete configuration.extraSurchargeAmount;
  delete configuration.promotionDiscountPercent;

  return configuration;
};

function RidePricing() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [asideWidth, setAsideWidth] = useState(0);
  const demandEnabled = Form.useWatch(['configuration', 'demandBandPercentsEnabled'], form);
  const scopeType = Form.useWatch('scopeType', form) || 'GLOBAL';
  const watchedPlatformFeePercent = Form.useWatch('platformFeePercent', form);
  const watchedReferralCommissionPercent = Form.useWatch('referralCommissionPercent', form);
  const platformFeePercentValue = Number(watchedPlatformFeePercent) || 0;
  const referralCommissionPercentValue = Number(watchedReferralCommissionPercent) || 0;
  const driverPercentValue = Math.round((100 - platformFeePercentValue - referralCommissionPercentValue) * 100) / 100;

  // Ant Design portals modals to document.body, so its normal centering does
  // not account for Keen's fixed sidebar. Read the sidebar's rendered width
  // and keep the dialog inside the unobscured content area instead.
  useEffect(() => {
    const updateAsideWidth = () => {
      const isDesktop = window.matchMedia('(min-width: 992px)').matches;
      const aside = document.getElementById('kt_aside');
      const minimized = document.body.classList.contains('aside-minimize');
      const hoveringMinimizedAside = document.body.classList.contains('aside-minimize-hover');
      const width = isDesktop && aside && (!minimized || hoveringMinimizedAside)
        ? Math.round(aside.getBoundingClientRect().width)
        : 0;
      setAsideWidth((current) => current === width ? current : width);
    };

    updateAsideWidth();
    const bodyObserver = new MutationObserver(updateAsideWidth);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const aside = document.getElementById('kt_aside');
    const asideObserver = typeof ResizeObserver === 'undefined' || !aside ? null : new ResizeObserver(updateAsideWidth);
    asideObserver?.observe(aside);
    window.addEventListener('resize', updateAsideWidth);
    return () => {
      bodyObserver.disconnect();
      asideObserver?.disconnect();
      window.removeEventListener('resize', updateAsideWidth);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const response = await ridePricingApi.getPolicies({ pageSize: 100 }); setItems(response?.items || []); }
    catch (error) { setItems([]); ToastHelper.showError(error?.response?.data?.message || 'Không thể tải biểu giá'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // baseFare/minimumFarePerSeat are gone from the form (product decision:
  // xe ghép always goes far enough that an opening fee / price floor no
  // longer applies), but they're still real NOT NULL columns underneath —
  // forced to 0 here on every path (fresh create, copy-from-active, edit)
  // so a copied/edited row can never smuggle its old nonzero value back in
  // through the now-hidden fields, where the admin has no UI left to
  // notice or correct it. includedDistanceMeters ("quãng đường đã gồm
  // trong giá mở cửa") retires along with baseFare for the same reason —
  // it only meant anything as the free distance a nonzero opening fee
  // already covered; with baseFare pinned at 0 a leftover nonzero value
  // here creates a genuinely free first N meters on every trip (found
  // live: a 1.5km ride priced at 0đ with a stale 2000 carried over from
  // the pre-removal policy).
  const showCreate = () => { setEditing(null); form.setFieldsValue({ ...defaults, configuration: configToFormValues({}) }); setOpen(true); };
  const showCreateFromActive = (row) => {
    setEditing(null);
    form.setFieldsValue({ ...defaults, ...row, baseFare: 0, minimumFarePerSeat: 0, includedDistanceMeters: 0, configuration: configToFormValues(row.configuration) });
    setOpen(true);
  };
  const showEdit = (row) => { setEditing(row); form.setFieldsValue({ ...defaults, ...row, baseFare: 0, minimumFarePerSeat: 0, includedDistanceMeters: 0, configuration: configToFormValues(row.configuration) }); setOpen(true); };
  // "Lưu & áp dụng ngay" is the button admins actually want — saving a
  // number that silently sits as a draft until a second, separate
  // "Kích hoạt" click is a trap ("tưởng lưu xong là xong"). Chaining
  // create/update straight into activate makes Lưu mean what it says;
  // "Chỉ lưu nháp" stays only for admins deliberately preparing ahead.
  const save = async (activateAfter) => {
    try {
      const values = await form.validateFields();
      // "Tổng phí EVG" is no longer typed separately — it's always exactly
      // EVG nhận + Hoa hồng người giới thiệu, computed here instead of
      // trusting a second manual field that could drift out of sync (same
      // rule the backend still enforces in normalizeFeeRates).
      const platformFeePercent = Number(values.platformFeePercent) || 0;
      const referralCommissionPercent = Number(values.referralCommissionPercent) || 0;
      const totalPlatformFeePercent = Math.round((platformFeePercent + referralCommissionPercent) * 100) / 100;
      if (totalPlatformFeePercent > 100) {
        ToastHelper.showError(`EVG nhận (${platformFeePercent}%) + Hoa hồng người giới thiệu (${referralCommissionPercent}%) không được vượt quá 100%`);
        return;
      }
      setSaving(true);
      const payload = {
        ...values,
        totalPlatformFeePercent,
        baseFare: 0,
        minimumFarePerSeat: 0,
        includedDistanceMeters: 0,
        configuration: buildConfiguration(values, editing?.configuration),
        ...(editing ? { expectedVersion: editing.version } : {}),
      };
      const response = editing
        ? await ridePricingApi.updateDraft(editing.pricingPolicyId, payload)
        : await ridePricingApi.createDraft(payload);
      if (activateAfter) {
        const pricingPolicyId = response?.policy?.pricingPolicyId ?? editing?.pricingPolicyId;
        try {
          await ridePricingApi.activate(pricingPolicyId);
          ToastHelper.showSuccess('Đã lưu và áp dụng biểu giá ngay');
        } catch (activateError) {
          ToastHelper.showError(activateError?.response?.data?.message || 'Đã lưu nhưng chưa áp dụng được — bấm "Kích hoạt" ở bảng bên dưới để thử lại');
        }
      } else {
        ToastHelper.showSuccess('Đã lưu bản nháp, chưa áp dụng');
      }
      setOpen(false); await load();
    } catch (error) {
      if (!error?.errorFields) ToastHelper.showError(error?.response?.data?.message || 'Không thể lưu biểu giá');
    } finally { setSaving(false); }
  };
  const activate = async (row) => {
    try { await ridePricingApi.activate(row.pricingPolicyId); ToastHelper.showSuccess('Đã áp dụng biểu giá'); await load(); }
    catch (error) { ToastHelper.showError(error?.response?.data?.message || 'Không thể áp dụng biểu giá'); }
  };

  const columns = useMemo(() => [
    {
      title: 'Biểu giá',
      width: '20%',
      align: 'left',
      render: (_, row) => (
        <Text strong style={{ fontSize: 17, color: '#222222', lineHeight: '24px' }}>{row.name}</Text>
      ),
    },
    {
      title: 'Phạm vi',
      width: '18%',
      align: 'left',
      render: (_, row) => (row.scopeType === 'GLOBAL' ? (
        <Tag>Toàn quốc</Tag>
      ) : (
        <Tooltip title={corridorLabel(row.scopeCode)}>
          <Tag
            color="blue"
            style={{
              whiteSpace: 'nowrap',
              fontSize: 16,
              lineHeight: '26px',
              padding: '4px 10px',
            }}
          >
            {corridorLabel(row.scopeCode)}
          </Tag>
        </Tooltip>
      )),
    },
    {
      title: 'Giá',
      width: '20%',
      align: 'left',
      render: (_, row) => {
        if (row.scopeType === 'CORRIDOR') {
          return <Text strong style={{ fontSize: 16, color: '#222222' }}>{money(row.flatFarePerSeat)}/ghế</Text>;
        }
        const extras = [
          Number(row.baseFare) > 0 && `Mở cửa ${money(row.baseFare)}`,
          Number(row.pricePerMinute) > 0 && `${money(row.pricePerMinute)}/phút`,
          Number(row.minimumFarePerSeat) > 0 && `Tối thiểu ${money(row.minimumFarePerSeat)}/ghế`,
        ].filter(Boolean);
        return (
          <div>
            <Text strong style={{ fontSize: 16, color: '#222222' }}>{money(row.pricePerKm)}/km</Text>
            {extras.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 14, fontWeight: 400, color: '#999999' }}>{extras.join(' · ')}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <>Bao xe<br />4 chỗ</>,
      width: '10%',
      align: 'center',
      render: (_, row) => (
        <Text style={{ fontSize: 16, fontWeight: 600, color: '#222222' }}>
          {Number(row.charterPricePercent4 ?? row.charterPricePercent ?? 100)}%
        </Text>
      ),
    },
    {
      title: <>Bao xe<br />7 chỗ</>,
      width: '10%',
      align: 'center',
      render: (_, row) => (
        <Text style={{ fontSize: 16, fontWeight: 600, color: '#222222' }}>
          {Number(row.charterPricePercent7 ?? row.charterPricePercent ?? 100)}%
        </Text>
      ),
    },
    {
      title: 'Trạng thái',
      width: '22%',
      align: 'left',
      render: (_, row) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 28, flexWrap: 'nowrap' }}>
          {statusTag(row.status)}
          {row.status === 'DRAFT' && <Button size="middle" onClick={() => showEdit(row)}>Sửa</Button>}
          {row.status === 'ACTIVE' && (
            <Button size="middle" onClick={() => showCreateFromActive(row)}>
              Sửa
            </Button>
          )}
          {row.status === 'DRAFT' && (
            <Popconfirm
              title="Kích hoạt biểu giá này?"
              description="Biểu giá đang áp dụng cùng phạm vi sẽ được lưu trữ. Biểu giá đã kích hoạt không thể sửa."
              okText="Kích hoạt"
              cancelText="Hủy"
              onConfirm={() => activate(row)}
            >
              <Button size="middle" type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }}>
                Kích hoạt
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ], []);

  return <KT01BaseLayout isFullScreen><div className="container-fluid px-4 my-4">
    <Card>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div><Title level={4} className="mb-0">Cấu hình giá xe ghép</Title></div>
        <Space><Button onClick={load} loading={loading}>Làm mới</Button><Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={showCreate}>Tạo biểu giá</Button></Space>
      </div>
      <div className="ride-pricing-table">
        <style>{`
          .ride-pricing-table .ant-table {
            width: 100%;
          }
          .ride-pricing-table .ant-table-thead > tr > th {
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
            color: #252525;
            vertical-align: middle;
            padding: 22px 24px 24px;
            background: #fff;
          }
          .ride-pricing-table .ant-table-thead > tr > th::before {
            display: none !important;
          }
          .ride-pricing-table .ant-table-tbody > tr > td {
            padding: 24px 24px;
            vertical-align: middle;
            border-bottom: 1px solid #eeeeee;
          }
          .ride-pricing-table .ant-table-tbody .ant-tag {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 32px;
            padding: 0 12px;
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            background: #fff;
            font-size: 14px;
            color: #555555;
          }
        `}</style>
        <Table
          rowKey="pricingPolicyId"
          dataSource={items.filter((item) => item.status !== 'ARCHIVED')}
          columns={columns}
          loading={loading}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={false}
        />
      </div>
    </Card>
    <Modal
      title={editing ? 'Sửa biểu giá nháp' : 'Tạo biểu giá mới'}
      width={asideWidth ? `calc(100% - ${asideWidth + 48}px)` : '90%'}
      style={{
        maxWidth: 1120,
        top: 24,
        // The left and right margins reserve space for the expanded sidebar.
        // With the sidebar minimized, retain Ant Design's usual centered modal.
        ...(asideWidth ? { margin: `0 24px 0 ${asideWidth + 24}px` } : {}),
      }}
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingRight: 8 } }}
      open={open}
      onCancel={() => setOpen(false)}
      destroyOnClose
      footer={<Space>
        <Button onClick={() => setOpen(false)}>Đóng</Button>
        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} loading={saving} onClick={() => save(true)}>Lưu &amp; áp dụng ngay</Button>
      </Space>}
    >
      <Form form={form} layout="vertical" initialValues={{ ...defaults, configuration: configToFormValues({}) }}>
        <Form.Item name="priority" hidden initialValue={0}><InputNumber /></Form.Item>
        <Form.Item name="name" label="Tên biểu giá (để admin dễ nhận biết, khách không thấy)" rules={[{ required: true, message: 'Nhập tên biểu giá' }]}>
          <Input placeholder="Ví dụ: Biểu giá toàn quốc tháng 08/2026" />
        </Form.Item>
        <Form.Item name="scopeType" label="Phạm vi áp dụng" rules={[{ required: true }]}>
          <Select
            disabled={!!editing}
            onChange={(value) => {
              if (value === 'CORRIDOR') form.setFieldsValue({ scopeCode: CORRIDOR_OPTIONS[0].value });
            }}
            options={[
              { value: 'GLOBAL', label: 'Toàn quốc — tính theo km' },
              { value: 'CORRIDOR', label: 'Liên tỉnh Hà Nội - Nam Định — giá cố định mỗi ghế' },
            ]}
          />
        </Form.Item>
        {scopeType === 'CORRIDOR' ? <>
          <Form.Item name="scopeCode" hidden initialValue={CORRIDOR_OPTIONS[0].value}><Input /></Form.Item>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="flatFarePerSeat" label="Giá cố định mỗi ghế" rules={[{ required: true, message: 'Nhập giá cố định mỗi ghế' }]}>
                <InputNumber
                  min={1}
                  step={5000}
                  addonAfter="₫/ghế"
                  style={{ width: '100%' }}
                  formatter={(value) => (value == null || value === '' ? '' : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
                  parser={(value) => (value ? value.replace(/\./g, '') : '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="charterPricePercent4"
                label="Giá bao xe 4 chỗ (% giá bình thường)"
                extra="Khách bao nguyên xe 4 chỗ trả % này trên giá tính bình thường (giá/ghế × 4 ghế). 100% = không giảm giá."
                rules={[{ required: true, message: 'Nhập % giá bao xe 4 chỗ' }]}
              >
                <InputNumber min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="charterPricePercent7"
                label="Giá bao xe 7 chỗ (% giá bình thường)"
                extra="Khách bao nguyên xe 7 chỗ trả % này trên giá tính bình thường (giá/ghế × 7 ghế). 100% = không giảm giá."
                rules={[{ required: true, message: 'Nhập % giá bao xe 7 chỗ' }]}
              >
                <InputNumber min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}><Form.Item name="platformFeePercent" label="EVG nhận (%)" extra="Phần EVG thực nhận trên mỗi cuốc xe." rules={[{ required: true, message: 'Nhập % EVG nhận' }]}><InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item name="referralCommissionPercent" label="Hoa hồng người giới thiệu khách hàng (%)" extra="Chỉ trả khi khách hàng (người đặt chuyến) có người giới thiệu hợp lệ." rules={[{ required: true, message: 'Nhập % hoa hồng' }]}><InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Tài xế nhận (%)" extra="Tự động tính = 100% − (EVG + Hoa hồng) — không cần nhập."><InputNumber value={driverPercentValue} disabled addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="pricePerKm" hidden initialValue={0}><InputNumber /></Form.Item>
        </> : <>
        <Alert
          className="mb-3"
          type="info"
          message={<span>Công thức tính giá 1 khách: <Text strong>Số km × Giá mỗi km</Text>, nhân thêm hệ số theo giờ/loại xe bên dưới (nếu có).</span>}
        />
        <Row gutter={16}>
          <Col xs={24}><Form.Item name="pricePerKm" label="1. Giá mỗi km" extra="Cộng thêm theo số km xe chạy." rules={[{ required: true, message: 'Nhập giá mỗi km' }]}><InputNumber min={0} step={100} addonAfter="₫/km" style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="charterPricePercent4"
              label="Giá bao xe 4 chỗ (% giá bình thường)"
              extra="Khách bao nguyên xe 4 chỗ trả % này trên giá tính bình thường (giá/ghế × 4 ghế). 100% = không giảm giá. VD 90% = rẻ hơn 10% so với mua lẻ từng ghế."
              rules={[{ required: true, message: 'Nhập % giá bao xe 4 chỗ' }]}
            >
              <InputNumber min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="charterPricePercent7"
              label="Giá bao xe 7 chỗ (% giá bình thường)"
              extra="Khách bao nguyên xe 7 chỗ trả % này trên giá tính bình thường (giá/ghế × 7 ghế). 100% = không giảm giá."
              rules={[{ required: true, message: 'Nhập % giá bao xe 7 chỗ' }]}
            >
              <InputNumber min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={8}><Form.Item name="platformFeePercent" label="2. EVG nhận (%)" extra="Phần EVG thực nhận trên mỗi cuốc xe." rules={[{ required: true, message: 'Nhập % EVG nhận' }]}><InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item name="referralCommissionPercent" label="3. Hoa hồng người giới thiệu khách hàng (%)" extra="Chỉ trả khi khách hàng (người đặt chuyến) có người giới thiệu hợp lệ — trả mỗi lần khách đó đi xe, không phải 1 lần duy nhất." rules={[{ required: true, message: 'Nhập % hoa hồng' }]}><InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item label="4. Tài xế nhận (%)" extra="Tự động tính = 100% − (EVG + Hoa hồng) — không cần nhập."><InputNumber value={driverPercentValue} disabled addonAfter="%" style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        </>}
        <Form.Item name="baseFare" hidden initialValue={0}><InputNumber /></Form.Item>
        <Form.Item name="minimumFarePerSeat" hidden initialValue={0}><InputNumber /></Form.Item>
        {scopeType === 'CORRIDOR' ? null : <>
        <Divider />
        <Title level={5}>5. Hệ số theo giờ khởi hành</Title>
        <Text type="secondary" className="d-block mb-2">Nhân thêm vào giá theo khung giờ xe khởi hành. Ví dụ 1.10 = +10%. Nếu để trống bảng, hệ thống dùng khung giờ mặc định theo tài liệu định giá (như đang hiện sẵn).</Text>
        <Form.List name={['configuration', 'timeBandMultipliers']}>
          {(fields, { add, remove }) => <>
            {fields.map((field) => <Row gutter={8} key={field.key} align="middle" className="mb-2">
              <Col span={6}><Form.Item {...field} name={[field.name, 'from']} noStyle rules={[{ required: true, message: 'Nhập giờ bắt đầu' }, { pattern: TIME_PATTERN, message: 'Định dạng HH:MM' }]}><Input placeholder="Từ giờ, VD 18:00" /></Form.Item></Col>
              <Col span={6}><Form.Item {...field} name={[field.name, 'to']} noStyle rules={[{ required: true, message: 'Nhập giờ kết thúc' }, { pattern: TIME_PATTERN, message: 'Định dạng HH:MM' }]}><Input placeholder="Đến giờ, VD 21:00" /></Form.Item></Col>
              <Col span={8}><Form.Item {...field} name={[field.name, 'multiplier']} noStyle rules={[{ required: true, message: 'Nhập hệ số' }]}><InputNumber min={0.1} max={5} step={0.05} addonBefore="×" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={4}><Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} /></Col>
            </Row>)}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ from: '', to: '', multiplier: 1 })}>Thêm khung giờ</Button>
          </>}
        </Form.List>

        <Divider />
        <Title level={5}>6. Phụ phí lệch tuyến</Title>
        <Text type="secondary" className="d-block mb-2">Đón/trả khách càng xa tuyến chính của tài xế thì cộng thêm phụ phí (cộng 1 lần cho cả chuyến, không nhân theo số khách). Xa hơn mức cao nhất trong bảng vẫn tính theo mức cao nhất, không bị chặn báo giá.</Text>
        <Form.List name={['configuration', 'detourSurchargeTiersKm']}>
          {(fields, { add, remove }) => <>
            {fields.map((field) => <Row gutter={8} key={field.key} align="middle" className="mb-2">
              <Col span={8}><Form.Item {...field} name={[field.name, 'maxKm']} noStyle rules={[{ required: true, message: 'Nhập số km' }]}><InputNumber min={0} step={1} addonBefore="Lệch tới" addonAfter="km" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={10}><Form.Item {...field} name={[field.name, 'surcharge']} noStyle rules={[{ required: true, message: 'Nhập phụ phí' }]}><InputNumber min={0} step={1000} addonBefore="Cộng thêm" addonAfter="₫" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={4}><Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} /></Col>
            </Row>)}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ maxKm: 0, surcharge: 0 })}>Thêm mức lệch tuyến</Button>
          </>}
        </Form.List>

        <Divider />
        <Title level={5}>7. Biên độ giá theo nhu cầu (Giá sàn/Giá trần theo %)</Title>
        <Form.Item name={['configuration', 'demandBandPercentsEnabled']} valuePropName="checked" className="mb-2">
          <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
        </Form.Item>
        {demandEnabled ? <>
          <Text type="secondary" className="d-block mb-2">% tính trên Giá chuẩn (100% = giá tính theo mục 1, trước khi nhân hệ số giờ/xe) của từng chuyến — áp dụng cho mọi quãng đường, không riêng 1 tuyến. Số liệu mặc định lấy theo tài liệu định giá.</Text>
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message="Khi nào áp dụng mức nào?"
            description={<div>
              <div>• <Text strong>Đặt sớm</Text>: khách đặt trước ≥24h so với giờ khởi hành, và chuyến chưa đầy quá 50% ghế.</div>
              <div>• <Text strong>Giờ đông khách</Text>: chuyến đã có ≥50% số ghế được đặt.</div>
              <div>• <Text strong>Giờ cao điểm</Text>: chuyến đã có ≥80% số ghế được đặt.</div>
              <div>• <Text strong>Còn 1 ghế cuối</Text>: xe chỉ còn đúng 1 ghế trống — luôn được ưu tiên áp dụng trước các mức trên.</div>
              <div>• Ngoài các trường hợp trên: dùng <Text strong>Giá chuẩn</Text> (100%).</div>
              <div className="mt-1">Thứ tự ưu tiên khi trùng nhiều điều kiện: <Text code>Còn 1 ghế cuối &gt; Cao điểm &gt; Đông khách &gt; Đặt sớm &gt; Chuẩn</Text>.</div>
              <div className="mt-1">Mỗi mức nhập 2 số (từ–đến) nhưng hệ thống chỉ tính giá bằng <Text strong>số ở giữa</Text> (ví dụ 91.6%–95.6% → tính 93.6%) — 2 số chỉ để thấy biên độ dao động.</div>
            </div>}
          />
          <Row gutter={[16, 16]} className="mb-3">
            <Col xs={24} sm={8}>
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Text strong style={{ fontSize: 13, color: '#1E293B' }}>Giá sàn (Floor)</Text>
                  <span style={{ fontSize: 11, color: '#64748B' }}>Mức tối thiểu</span>
                </div>
                <Form.Item
                  name={['configuration', 'demandBandPercents', 'floor']}
                  noStyle
                  rules={[{ required: true, message: 'Nhập %' }]}
                >
                  <InputNumber min={1} max={100} step={0.1} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Text strong style={{ fontSize: 13, color: '#1E293B' }}>Giá trần (Ceiling)</Text>
                  <span style={{ fontSize: 11, color: '#64748B' }}>Mức tối đa</span>
                </div>
                <Form.Item
                  name={['configuration', 'demandBandPercents', 'ceiling']}
                  noStyle
                  rules={[{ required: true, message: 'Nhập %' }]}
                >
                  <InputNumber min={100} max={500} step={0.1} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Text strong style={{ fontSize: 13, color: '#1E293B' }}>Giá chuẩn (Standard)</Text>
                  <span style={{ fontSize: 11, color: '#64748B' }}>Mốc cố định</span>
                </div>
                <InputNumber value={100} disabled addonAfter="%" style={{ width: '100%' }} />
              </div>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <DemandScenarioCard
                title="Đặt sớm"
                condition="Khách đặt trước ≥24h &amp; chuyến dưới 50% ghế"
                priority="Ưu tiên 4"
                tagColor="blue"
                field="earlyBooking"
              />
            </Col>
            <Col xs={24} md={12}>
              <DemandScenarioCard
                title="Giờ đông khách"
                condition="Chuyến đã có ≥50% số ghế được đặt"
                priority="Ưu tiên 3"
                tagColor="orange"
                field="busy"
              />
            </Col>
            <Col xs={24} md={12}>
              <DemandScenarioCard
                title="Giờ cao điểm"
                condition="Chuyến đã có ≥80% số ghế được đặt"
                priority="Ưu tiên 2"
                tagColor="volcano"
                field="peak"
              />
            </Col>
            <Col xs={24} md={12}>
              <DemandScenarioCard
                title="Còn 1 ghế cuối"
                condition="Chuyến xe chỉ còn đúng 1 ghế trống duy nhất"
                priority="Ưu tiên 1"
                tagColor="red"
                field="lastSeat"
              />
            </Col>
          </Row>
        </> : <Text type="secondary">Đang tắt — không có giá sàn/giá trần, giá chỉ tính theo mục 1 và các hệ số bên dưới.</Text>}

        <Divider />
        <Title level={5}>8. Hệ số theo số ghế xe</Title>
        <Text type="secondary" className="d-block mb-2">Nhân thêm giá theo số ghế của xe tài xế đăng ký (ví dụ xe 7 chỗ tính cao hơn xe 4 chỗ). Để trống bảng = không phân biệt loại xe.</Text>
        <Form.List name={['configuration', 'seatCapacityMultipliersList']}>
          {(fields, { add, remove }) => <>
            {fields.map((field) => <Row gutter={8} key={field.key} align="middle" className="mb-2">
              <Col span={8}><Form.Item {...field} name={[field.name, 'seats']} noStyle rules={[{ required: true, message: 'Nhập số ghế' }]}><InputNumber min={1} max={16} addonBefore="Xe" addonAfter="chỗ" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={10}><Form.Item {...field} name={[field.name, 'multiplier']} noStyle rules={[{ required: true, message: 'Nhập hệ số' }]}><InputNumber min={0.1} max={5} step={0.05} addonBefore="×" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={4}><Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} /></Col>
            </Row>)}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ seats: 4, multiplier: 1 })}>Thêm loại xe</Button>
          </>}
        </Form.List>
        </>}

        <Form.Item name="includedDistanceMeters" hidden initialValue={defaults.includedDistanceMeters}><InputNumber /></Form.Item>
        <Form.Item name="pricePerMinute" hidden initialValue={defaults.pricePerMinute}><InputNumber /></Form.Item>
        <Form.Item name="maximumFarePerSeat" hidden initialValue={defaults.maximumFarePerSeat}><InputNumber /></Form.Item>
        <Form.Item name="roundingUnit" hidden initialValue={defaults.roundingUnit}><InputNumber /></Form.Item>
        <Form.Item name="surgeEnabled" hidden initialValue={defaults.surgeEnabled} valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="surgeMultiplier" hidden initialValue={defaults.surgeMultiplier}><InputNumber /></Form.Item>
      </Form>
      {editing && <Descriptions size="small" column={2}><Descriptions.Item label="Mã">{editing.code}</Descriptions.Item><Descriptions.Item label="Phiên bản">v{editing.version}</Descriptions.Item></Descriptions>}
    </Modal>
  </div></KT01BaseLayout>;
}

export default RidePricing;
